import { useState, useEffect, useCallback } from "react";
import { useEntityOperations } from "./useEntityOperations";
import { ChallengeService } from "../services/challengeService";
import {
  Challenge,
  CreateChallengeInput,
  UpdateChallengeInput,
} from "../types/challenge";

// Hook for managing challenges list
export const useChallenges = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ChallengeService.getChallenges();

      if (response) {
        setChallenges(response);
      } else {
        setError("Failed to fetch challenges");
        setChallenges([]);
      }
    } catch (err) {
      setError("An unexpected error occurred while fetching challenges");
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh challenges list
  const refresh = useCallback(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Initial fetch
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    challenges,
    loading,
    error,
    refresh,
  };
};

// Hook for managing individual challenge operations
export const useChallengeOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const createChallenge = useCallback(
    (challengeData: CreateChallengeInput) =>
      run(
        () => ChallengeService.createChallenge(challengeData),
        "Failed to create challenge"
      ),
    [run]
  );

  const updateChallenge = useCallback(
    (challengeId: number, updates: UpdateChallengeInput) =>
      run(
        () => ChallengeService.updateChallenge(challengeId, updates),
        "Failed to update challenge"
      ),
    [run]
  );

  const deleteChallenge = useCallback(
    (challengeId: number) =>
      runBoolean(
        () => ChallengeService.deleteChallenge(challengeId),
        "Failed to delete challenge"
      ),
    [runBoolean]
  );

  // clearError comes from the factory - this hook was the one that forgot it.
  return { createChallenge, updateChallenge, deleteChallenge, ...rest };
};
